import type { DropzoneOptions } from "react-dropzone";
import type { Control, FieldPath, FieldValues } from "react-hook-form";

import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import type { UserProfile } from "@/types/user";

import { useUpdateProfilePicture } from "@/api/auth-query";
import { useUpdateUserProfile } from "@/api/users-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
	FileInput,
	FileUploader
} from "@/components/ui/file-upload";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";

import PhoneNumberInput from "../form/phone-input";

const editProfileSchema = z.object({
	businessName: z.string().nonempty("Business name is required"),
	address: z.string().nonempty("Address is required"),
	email: z.string().email("Invalid email address").nonempty("Email is required"),
	phoneNumber: z.string().nonempty("Phone number is required"),
	photo: z.any()

});

export default function EditProfile({ userProfile }: { userProfile: UserProfile | undefined }) {
	const [files, setFiles] = useState<File[] | null>(null);

	const form = useForm<z.infer<typeof editProfileSchema>>({
		resolver: zodResolver(editProfileSchema),
		defaultValues: {
			businessName: userProfile?.businessName,
			address: userProfile?.address || "",
			email: userProfile?.email || "",
			phoneNumber: userProfile?.phoneNumber || ""
		}
	});
	const updateQuery = useUpdateUserProfile();
	const handleSubmit = (values: z.infer<typeof editProfileSchema>) => {
		updateQuery.mutateAsync(values);
	};
	const updateProfilePicture = useUpdateProfilePicture();

	useEffect(() => {
		if (files && files.length) {
			const formData = new FormData();
			formData.append("hasFile", "true");
			formData.append("file", files[0]);
			updateProfilePicture.mutateAsync(formData);

			setFiles(null);
			form.resetField("photo");
		}
	}, [files]);

	return (
		<Dialog onOpenChange={() => form.reset()}>
			<DialogTrigger asChild>
				<Button disabled={!userProfile}>Edit profile</Button>
			</DialogTrigger>
			<DialogContent className=" max-w-3xl">
				<DialogDescription className="sr-only">Edit Profile</DialogDescription>
				<DialogHeader>
					<DialogTitle className="text-xl font-semibold">Edit Profile</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 py-4">
						<div className="space-y-2 items-center">
							<Label>Profile Picture</Label>
							<div className="flex gap-2">
								<div className="relative h-[100px] w-[100px]">
									<img
										src={
											userProfile?.profileImage
												? userProfile.profileImage
												: files && files.length > 0
													? URL.createObjectURL(files[0])
													: "/placeholder.svg"
										}
										alt="Profile picture"
										className="rounded-lg object-cover h-full w-full"
									/>
								</div>
								<UploadFile
									files={files}
									setFiles={setFiles}
									name="photo"
									accept="image/png,image/jpg,image/jpeg"
									control={form.control}
									dropZoneConfig={{
										maxFiles: 1,
										maxSize: 1024 * 1024 * 4,
										multiple: false
									}}
								/>
							</div>
						</div>
						<FormField
							control={form.control}
							name="businessName"
							render={({ field }) => (
								<FormItem>
									<Label htmlFor="businessName">
										Business name
										{" "}
										<span className="text-red-500">*</span>
									</Label>
									<FormControl>
										<Input className="h-14" id="businessName" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="address"
							render={({ field }) => (
								<FormItem>
									<Label htmlFor="address">
										Address
										{" "}
										<span className="text-red-500">*</span>
									</Label>
									<FormControl>
										<Input className="h-14" id="address" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
									<Label htmlFor="email">
										Email address
										{" "}
										<span className="text-red-500">*</span>
									</Label>
									<FormControl>
										<Input className="h-14" id="email" type="email" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						{/* <FormField
							control={form.control}
							name="phone"
							render={({ field }) => (
								<FormItem>
									<Label htmlFor="phone">
										Phone number
										{" "}
										<span className="text-red-500">*</span>
									</Label>
									<FormControl>
										<Input id="phone" type="tel" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/> */}
						<FormField
							control={form.control}
							name="phoneNumber"
							render={({ field }) => (
								<FormItem>
									<Label htmlFor="phone">
										Phone number
										{" "}
										<span className="text-red-500">*</span>
									</Label>
									<FormControl>
										<PhoneNumberInput {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{userProfile?.socials?.map(social => (
							<div key={social.name} className="space-y-2">
								<Label htmlFor={social.name}>{social.name}</Label>
								<Input
									id={social.name}
									defaultValue={social.url}
									placeholder="https://example.com"
								/>
							</div>
						))}

						<div className="flex justify-between">
							<Button
								type="button"
								variant="outline"
								onClick={() => form.reset()}
							>
								Discard
							</Button>
							<Button
								type="submit"
								// disabled={!form.formState.isValid}
							>
								Save changes
							</Button>
						</div>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}

interface UploadFileProps<T extends FieldValues> {
	files: File[] | null;
	setFiles: (files: File[] | null) => void;
	accept?: string;
	className?: string;
	name: FieldPath<T>;
	control: Control<T>;
	dropZoneConfig?: DropzoneOptions;
}

export function UploadFile<T extends FieldValues>({
	files,
	setFiles,
	className,
	name,
	control,
	dropZoneConfig
}: UploadFileProps<T>) {
	dropZoneConfig ??= {
		maxFiles: 15,
		maxSize: 1024 * 1024 * 4,
		multiple: true
	};

	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<FormItem>
					<FormControl>
						<FileUploader
							{...field}
							value={files}
							onValueChange={setFiles}
							dropzoneOptions={dropZoneConfig}
							className={cn("relative bg-background rounded-lg p-1")}
						>
							{/* <FileUploaderContent className="flex flex-row flex-wrap gap-2">
								{files && files.length > 0 && files.map((file, i) => (
									<FileUploaderItem
										key={crypto.randomUUID()}
										index={i}
										className="group relative size-32 rounded-lg border border-primary overflow-hidden"
									>
										<img
											src={URL.createObjectURL(file)}
											alt={file.name}
											className="w-full h-full object-cover"
										/>
									</FileUploaderItem>
								))}
							</FileUploaderContent> */}
							<FileInput
								id="fileInput"
								accept="image/*"
								className={cn("w-fit", className)}
							>
								<Button size="icon" variant="secondary" className="rounded-full">
									<Pencil className="h-4 w-4" />
									<span className="sr-only">Edit profile picture</span>
								</Button>
							</FileInput>
						</FileUploader>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	);
}
